// lib/koi-pond-v3/interaction/dragonGate.ts
//
// The Dragon Gate — one-time-per-session moon-click beat. A time-driven
// controller that reads dragonGatePhases envelopes and pushes them into the
// scene's existing systems (moon bloom, water shockwave, caustic flare,
// color-grade flash, firefly scatter, koi leap + shimmer). Every channel is
// 0 at rest so the dormant scene is untouched.

import { Vector2, Vector3, type PerspectiveCamera } from 'three';
import {
  dragonGateChannels, leapT01, leapWarp, DRAGON_GATE_DURATION, PH, type Vec3,
} from './dragonGatePhases';

export interface DragonGateKoi {
  position: Vector3;
  uniforms: { uShimmer: { value: number } };
  swimmer: { leap: (t: number, arc: { apex: Vec3; end: Vec3; duration?: number }) => void };
  // scaled up during the leap; layers toggled so the leaping koi composites
  // OVER the moon overlay pass (otherwise the moon disc paints on top of it).
  koiMesh: { scale: { setScalar: (s: number) => void }; layers: { set: (n: number) => void } };
  baseScale: number;                                       // resting scale to restore
}

export interface DragonGateDeps {
  camera: PerspectiveCamera;
  moonPos: Vector3;                                  // moon.moonGroupPos
  setMoonBloom: (v: number) => void;                 // moon.setBloom
  water: { uShockOrigin: { value: Vector2 }; uShockAge: { value: number }; uShockStr: { value: number } };
  moonReflXZ: Vector2;                               // where the shock originates (≈ below the moon)
  setCausticFlare: (v: number) => void;
  setColorFlash: (v: number) => void;
  scatterFireflies: (x: number, z: number, strength: number) => void;
  koi: DragonGateKoi[];
  triggerReflectionBreak: (t: number) => void;       // existing surface-break splash
  reduced: boolean;
}

export function makeDragonGate(deps: DragonGateDeps) {
  let startT = -1;
  let hero: DragonGateKoi | null = null;
  let leapFired = false;
  let scatterFired = false;
  let breakFired = false;

  // Once-per-page-LOAD guard (in-memory): fires once per scene mount; a reload
  // replays it (owner chose this over the old sessionStorage once-per-session so
  // visitors — and demos — can see it again). Still un-spammable: startT blocks
  // re-fire while a beat is running, and firedOnce blocks any later re-fire this
  // load.
  let firedOnce = false;
  const fired = () => firedOnce;

  function trigger(t: number) {
    if (startT >= 0 || firedOnce) return;
    firedOnce = true;
    startT = t;
    leapFired = scatterFired = breakFired = false;
    // hero koi = the one nearest the moon-reflection lane (most on-camera/lit).
    hero = deps.koi.reduce((a, b) =>
      a.position.distanceTo(new Vector3(deps.moonReflXZ.x, a.position.y, deps.moonReflXZ.y)) <
      b.position.distanceTo(new Vector3(deps.moonReflXZ.x, b.position.y, deps.moonReflXZ.y)) ? a : b
    );
  }

  function update(t: number) {
    if (startT < 0) return;
    const e = t - startT;
    if (e > DRAGON_GATE_DURATION) {
      // ease everything to rest, then stop
      deps.setMoonBloom(0); deps.setCausticFlare(0); deps.setColorFlash(0);
      deps.water.uShockStr.value = 0;
      if (hero) { hero.uniforms.uShimmer.value = 0; hero.koiMesh.scale.setScalar(hero.baseScale); hero.koiMesh.layers.set(0); }
      startT = -1; hero = null;
      return;
    }
    const c = dragonGateChannels(e, deps.reduced);
    deps.setMoonBloom(c.moonBloom);
    deps.setCausticFlare(c.caustic);
    deps.setColorFlash(c.flash);
    deps.water.uShockOrigin.value.copy(deps.moonReflXZ);
    deps.water.uShockAge.value = c.shockAge;
    deps.water.uShockStr.value = c.shock;
    if (hero) {
      const lt = leapT01(e);
      if (lt >= 0) {
        // hf plateaus at 1 across the apex HOLD (leapWarp), so the koi's scale +
        // shimmer hold at full while it's silhouetted on the moon — same warp
        // drives its position in the swimmer, so all three plateau together.
        const hf = Math.sin(Math.PI * leapWarp(lt));
        // Apex size is a small FIXED target (~1.4) so the koi reads as a fish
        // crossing the moon, not a frame-filling blimp (owner note 2026-06-14:
        // the old baseScale*3 ≈ 3.9x overran the moon entirely).
        hero.koiMesh.scale.setScalar(hero.baseScale + (1.4 - hero.baseScale) * hf);
        hero.uniforms.uShimmer.value = hf;
        hero.koiMesh.layers.set(1); // render in the moon overlay pass = ON TOP of the moon
      } else {
        hero.koiMesh.scale.setScalar(hero.baseScale);
        hero.uniforms.uShimmer.value = 0;
        hero.koiMesh.layers.set(0);
      }
    }

    // one-shot edges
    if (!scatterFired && c.scatter > 0.5 && !deps.reduced) {
      scatterFired = true;
      deps.scatterFireflies(deps.moonReflXZ.x, deps.moonReflXZ.y, 1.4);
    }
    if (!leapFired && leapT01(e) >= 0 && hero && !deps.reduced) {
      leapFired = true;
      const h = hero.position;
      const end = { x: h.x + 0.6, y: h.y, z: h.z - 0.3 };
      // P = the on-screen cross point: halfway along the camera→moon ray, so the
      // koi is silhouetted directly in front of the moon at mid-leap.
      const P = deps.camera.position.clone().add(
        deps.moonPos.clone().sub(deps.camera.position).multiplyScalar(0.3),
      );
      // The koi mesh origin sits below its body, so anchoring the origin on the
      // moon centre puts the visible body in the UPPER disc. Drop the apex point
      // so the body lands centred on the moon's face (owner note 2026-06-14).
      P.y -= 0.15;
      // Bézier control so curve(0.5) === P exactly (quad-bézier midpoint =
      // 0.25*start + 0.5*ctrl + 0.25*end ⟹ ctrl = 2P − ½(start+end)).
      const apex = {
        x: 2 * P.x - 0.5 * (h.x + end.x),
        y: 2 * P.y - 0.5 * (h.y + end.y),
        z: 2 * P.z - 0.5 * (h.z + end.z),
      };
      hero.swimmer.leap(t, { apex, end, duration: PH.leapEnd - PH.leapStart });
    }
    if (!breakFired && c.reflectionBreak > 0.4) {
      breakFired = true;
      deps.triggerReflectionBreak(t);
    }
  }

  return { trigger, update, hasFired: fired };
}
